import { useState, useEffect } from "react";
import { dataService } from "../services/dataService";

/**
 * New structure for field values:
 * field.value = {
 *   type: 'userFilled', // or 'default'
 *   data: {
 *     [userId]: {
 *       value: 'actual value',
 *       filledAt: timestamp,
 *       status: 'pending' | 'submitted' | 'approved' | 'rejected'
 *     }
 *   }
 * }
 */

export const useUserFieldData = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load documents on mount
  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setLoading(true);
        const docs = await dataService.getDocuments();
        setDocuments(docs || []);
        setError(null);
      } catch (err) {
        console.error("Error loading documents:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, []);

  // Update field value for a specific user
  const updateFieldValue = (documentId, userId, fieldId, value) => {
    setDocuments((prevDocs) => {
      return prevDocs.map((doc) => {
        if (doc.id !== documentId) return doc;

        const updatedFields = { ...doc.droppedFields };

        // Find and update the field across all pages
        Object.keys(updatedFields).forEach((pageNum) => {
          updatedFields[pageNum] = updatedFields[pageNum].map((field) => {
            if (field.id !== fieldId) return field;

            // Initialize field value structure if needed
            if (
              !field.value ||
              typeof field.value !== "object" ||
              !field.value.type
            ) {
              field.value = {
                // type: "userFilled",
                data: {},
              };
            }

            // Update user's value
            field.value.data[userId] = {
              value: value,
              // filledAt: new Date().toISOString(),
              status: field.value.data[userId]?.status || "pending",
            };

            return { ...field };
          });
        });

        return {
          ...doc,
          droppedFields: updatedFields,
        };
      });
    });
  };

  // Update user status for all fields they've filled in a document
  const updateUserStatus = (
    documentId,
    userId,
    status,
    additionalData = {}
  ) => {
    setDocuments((prevDocs) => {
      return prevDocs.map((doc) => {
        if (doc.id !== documentId) return doc;

        const updatedFields = { ...doc.droppedFields };

        Object.keys(updatedFields).forEach((pageNum) => {
          updatedFields[pageNum] = updatedFields[pageNum].map((field) => {
            if (field.value?.data?.[userId]) {
              field.value.data[userId] = {
                ...field.value.data[userId],
                status,
                ...additionalData,
                updatedAt: new Date().toISOString(),
              };
            }
            return { ...field };
          });
        });

        return {
          ...doc,
          droppedFields: updatedFields,
        };
      });
    });
  };

  // Get all user data for a document (computed from fields)
  const getUserData = (documentId, userId) => {
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) return {};

    const userData = {};

    Object.values(doc.droppedFields || {}).forEach((fields) => {
      fields.forEach((field) => {
        if (field.value?.data?.[userId]) {
          userData[field.id] = field.value.data[userId].value;
        }
      });
    });

    return userData;
  };

  // Get specific field value for a user
  const getFieldValue = (documentId, userId, fieldId) => {
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) return undefined;

    let fieldValue;

    Object.values(doc.droppedFields || {}).some((fields) => {
      const field = fields.find((f) => f.id === fieldId);
      if (field?.value?.data?.[userId]) {
        fieldValue = field.value.data[userId].value;
        return true;
      }
      return false;
    });

    return fieldValue;
  };

  // Get all users who have filled any field in a document
  const getDocumentUsers = (documentId) => {
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) return [];

    const usersSet = new Set();

    Object.values(doc.droppedFields || {}).forEach((fields) => {
      fields.forEach((field) => {
        if (field.value?.data) {
          Object.keys(field.value.data).forEach((userId) => {
            usersSet.add(userId);
          });
        }
      });
    });

    return Array.from(usersSet);
  };

  // Get submitted applicants (users with 'applicant-submitted' status)
  const getSubmittedApplicants = (documentId) => {
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) return [];

    const submittedUsers = new Set();

    Object.values(doc.droppedFields || {}).forEach((fields) => {
      fields.forEach((field) => {
        if (field.value?.data) {
          Object.entries(field.value.data).forEach(([userId, userData]) => {
            if (userData.status === "applicant-submitted") {
              submittedUsers.add(userId);
            }
          });
        }
      });
    });

    return Array.from(submittedUsers);
  };

  // Delete all user data from a document
  const deleteDocumentData = (documentId) => {
    setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== documentId));
  };

  // Delete specific user's data from a document
  const deleteUserData = (documentId, userId) => {
    setDocuments((prevDocs) => {
      return prevDocs.map((doc) => {
        if (doc.id !== documentId) return doc;

        const updatedFields = { ...doc.droppedFields };

        Object.keys(updatedFields).forEach((pageNum) => {
          updatedFields[pageNum] = updatedFields[pageNum].map((field) => {
            if (field.value?.data?.[userId]) {
              const newData = { ...field.value.data };
              delete newData[userId];

              return {
                ...field,
                value: {
                  ...field.value,
                  data: newData,
                },
              };
            }
            return field;
          });
        });

        return {
          ...doc,
          droppedFields: updatedFields,
        };
      });
    });
  };

  // Get user status from any field in the document
  const getUserStatus = (documentId, userId) => {
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) return null;

    let status = null;

    Object.values(doc.droppedFields || {}).some((fields) => {
      return fields.some((field) => {
        if (field.value?.data?.[userId]?.status) {
          status = field.value.data[userId].status;
          return true;
        }
        return false;
      });
    });

    return status;
  };

  // Save documents to backend/localStorage
  const saveDocuments = async () => {
    try {
      await dataService.saveDocuments(documents);
    } catch (err) {
      console.error("Error saving documents:", err);
      setError(err.message);
    }
  };

  // Auto-save when documents change
  useEffect(() => {
    if (!loading && documents.length >= 0) {
      const timer = setTimeout(saveDocuments, 500);
      return () => clearTimeout(timer);
    }
  }, [documents, loading]);

  // Compute userFieldData in the old format for backward compatibility
  const userFieldData = documents.reduce((acc, doc) => {
    acc[doc.id] = {};

    Object.values(doc.droppedFields || {}).forEach((fields) => {
      fields.forEach((field) => {
        if (field.value?.data) {
          Object.entries(field.value.data).forEach(([userId, userData]) => {
            if (!acc[doc.id][userId]) {
              acc[doc.id][userId] = {};
            }
            acc[doc.id][userId][field.id] = userData.value;
          });
        }
      });
    });

    return acc;
  }, {});

  return {
    userFieldData, // For backward compatibility
    documents,
    loading,
    error,
    updateFieldValue,
    updateUserStatus,
    getUserData,
    getFieldValue,
    deleteDocumentData,
    deleteUserData,
    getSubmittedApplicants,
    getDocumentUsers,
    getUserStatus,
    saveDocuments,
  };
};
